import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header, Modal, DataGrid } from '../layout';
import { useAuth } from '../hooks/useAuth';
import { useOrder } from '../context/OrderContext';
import Swal from 'sweetalert2';
import { api } from '../services/api';
import { setDocumentTitle } from '../utils/titleUtils';
import {
    FaEye,
    FaFileInvoice,
    FaTruck,
    FaBan,
    FaCheckCircle,
    FaClock,
    FaUser,
    FaCalendarAlt,
    FaShoppingCart,
    FaClipboardList,
    FaFilter,
    FaTimes,
    FaBoxOpen,
    FaEnvelope,
    FaCopy
} from 'react-icons/fa';

/* ─────────────────────────────────────────────
   STATUS CONFIG
───────────────────────────────────────────── */
const STATUS_CONFIG = {
    annullato: { label: 'Annullato', color: 'red', bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-200', dot: 'bg-red-500', icon: FaBan },
    completato: { label: 'Completato', color: 'emerald', bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500', icon: FaFileInvoice },
    consegnato: { label: 'Consegnato', color: 'blue', bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', dot: 'bg-blue-500', icon: FaTruck },
    confermato: { label: 'Confermato', color: 'amber', bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', dot: 'bg-amber-500', icon: FaCheckCircle },
    in_attesa: { label: 'In Attesa', color: 'gray', bg: 'bg-gray-100', text: 'text-gray-600', border: 'border-gray-200', dot: 'bg-gray-400', icon: FaClock },
};

const StatusPill = ({ statusKey, small = false }) => {
    const cfg = STATUS_CONFIG[statusKey] || STATUS_CONFIG.in_attesa;
    return (
        <span className={`inline-flex items-center gap-1.5 font-semibold rounded-full border ${cfg.bg} ${cfg.text} ${cfg.border} ${small ? 'text-[10px] px-2 py-0.5' : 'text-xs px-2.5 py-1'}`}>
            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${cfg.dot}`} />
            {cfg.label}
        </span>
    );
};

const isProspectOrder = (order) => Number(order?.is_prospect) === 1;

const ProspectBadge = () => (
    <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide bg-amber-100 text-amber-800 align-middle whitespace-nowrap">
        Nuovo cliente
    </span>
);

/* ─────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────── */
const getOrderStatusKey = (order) => {
    if (order.status === 'annullato' || order.Cancelled === '1') return 'annullato';
    if (order.Delivered === '1' && order.Invoiced === '1') return 'completato';
    if (order.Delivered === '1') return 'consegnato';
    if (order.ConfirmedDeliveryDate && order.ConfirmedDeliveryDate !== '') return 'confermato';
    return 'in_attesa';
};

// Un ordine si puo' duplicare e modificare solo finche' non e' consegnato, fatturato o annullato
const isEditableStatus = (statusKey) => statusKey === 'in_attesa' || statusKey === 'confermato';

const fmt = (v) => {
    if (!v && v !== 0) return '€ 0,00';
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(v);
};

const fmtDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

/* ─────────────────────────────────────────────
   MOBILE ORDER CARD
───────────────────────────────────────────── */
const OrderCard = ({ order, onView, onResendEmail, isAdmin }) => {
    const sk = getOrderStatusKey(order);
    const cfg = STATUS_CONFIG[sk];
    return (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden active:scale-[0.99] transition-transform">
            <div className={`h-1 w-full ${cfg.dot}`} />
            <div className="p-4">
                <div className="flex items-start justify-between gap-2 mb-3">
                    <div>
                        <p className={`font-mono font-bold text-base leading-tight ${order.has_progettazione ? 'text-blue-600' : 'text-gray-900'}`}>
                            {order.InternalOrdNo}
                        </p>
                        {order.ExternalOrdNo && (
                            <p className="text-[10px] text-gray-400 mt-0.5">APP: {order.ExternalOrdNo}</p>
                        )}
                        <p className="text-xs text-gray-400 mt-0.5">{fmtDate(order.OrderDate)}</p>
                    </div>
                    <StatusPill statusKey={sk} />
                </div>
                <div className="flex items-center gap-2 mb-3">
                    <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                        <FaUser className="text-gray-400 text-[10px]" />
                    </div>
                    <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-800">{order.CustomerName || order.Customer}{isProspectOrder(order) && <ProspectBadge />}</p>
                        <p className="text-xs text-gray-400">{order.SalespersonName || order.Salesperson || '—'}</p>
                    </div>
                </div>
                <div className="flex items-center justify-between pt-3 border-t border-gray-50">
                    <div>
                        <p className="text-xs text-gray-400 uppercase tracking-wider">Totale</p>
                        <p className="text-base font-bold text-gray-900 tabular-nums">{fmt(order.PayableAmount || order.total)}</p>
                    </div>
                    <div className="flex items-center gap-2">
                        {isAdmin && (
                        <button
                            onClick={() => onResendEmail(order)}
                            title="Reinvia email"
                            className="inline-flex items-center justify-center w-9 h-9 bg-blue-50 text-blue-600 rounded-xl text-sm shadow-sm hover:bg-blue-100 active:bg-blue-200 transition-colors"
                        >
                            <FaEnvelope className="text-xs" />
                        </button>
                        )}
                        <button
                            onClick={() => onView(order)}
                            className="inline-flex items-center gap-1.5 px-4 py-2 bg-red-500 text-white rounded-xl text-sm font-semibold shadow-sm hover:bg-red-600 active:bg-red-700 transition-colors"
                        >
                            <FaEye className="text-xs" />
                            Dettagli
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

/* ─────────────────────────────────────────────
   STAT CARD
───────────────────────────────────────────── */
const StatCard = ({ icon: Icon, label, value, color }) => (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
            <Icon className="text-base" />
        </div>
        <div className="min-w-0">
            <p className="text-[11px] text-gray-400 uppercase tracking-wider">{label}</p>
        </div>
    </div>
);

/* ─────────────────────────────────────────────
   FILTER PANEL
───────────────────────────────────────────── */
const FilterPanel = ({
    dateFrom, setDateFrom, dateTo, setDateTo,
    typeFilter, setTypeFilter,
    statusFilter, setStatusFilter, orders, isMobile, isOpen, onClose
}) => {
    const typeFilters = [
        { key: 'all', label: 'Tutti' },
        { key: 'ordine', label: 'Ordini' },
        { key: 'offerta', label: 'Offerte' },
    ];
    const getTypeCount = (key) => key === 'all' ? orders.length : orders.filter(o => (o.order_type || 'ordine') === key).length;
    const statusFilters = [
        { key: 'all', label: 'Tutti' },
        { key: 'in_attesa', label: 'In Attesa' },
        { key: 'confermato', label: 'Confermato' },
        { key: 'consegnato', label: 'Consegnato' },
        { key: 'completato', label: 'Completato' },
        { key: 'annullato', label: 'Annullato' },
    ];

    const getCount = (key) => key === 'all' ? orders.length : orders.filter(o => getOrderStatusKey(o) === key).length;

    const inner = (
        <div className="flex flex-col gap-5">
            {/* Type filter */}
            <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <FaClipboardList /> Tipo
                </label>
                <div className="flex flex-wrap gap-2">
                    {typeFilters.map(f => {
                        const active = typeFilter === f.key;
                        const count = getTypeCount(f.key);
                        return (
                            <button
                                key={f.key}
                                onClick={() => { setTypeFilter(f.key); isMobile && onClose(); }}
                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold border transition-all ${active
                                    ? 'bg-red-50 text-red-600 border-red-200'
                                    : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'
                                    }`}
                            >
                                {f.label}
                                <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${active ? 'bg-white/60' : 'bg-gray-100'}`}>{count}</span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Date range */}
            <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <FaCalendarAlt /> Periodo
                </label>
                <div className="gap-2">
                    <div>
                        <p className="text-[10px] text-gray-400 mb-1">Dal</p>
                        <input
                            type="date"
                            value={dateFrom}
                            onChange={e => setDateFrom(e.target.value)}
                            className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-red-400 focus:border-red-400 bg-gray-50 focus:bg-white transition"
                        />
                    </div>
                    <div>
                        <p className="text-[10px] text-gray-400 mb-1">Al</p>
                        <input
                            type="date"
                            value={dateTo}
                            onChange={e => setDateTo(e.target.value)}
                            className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-red-400 focus:border-red-400 bg-gray-50 focus:bg-white transition"
                        />
                    </div>
                </div>
            </div>

            {/* Status filter removed */}
        </div>
    );

    if (!isMobile) {
        return (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sticky top-4">
                <p className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <FaFilter className="text-red-400" /> Filtri
                </p>
                {inner}
            </div>
        );
    }

    return (
        <>
            <div
                className={`fixed inset-0 bg-black/40 z-40 transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                onClick={onClose}
            />
            <div className={`fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-3xl shadow-2xl transition-transform duration-300 ease-out ${isOpen ? 'translate-y-0' : 'translate-y-full'}`}>
                <div className="flex justify-center pt-3 pb-1">
                    <div className="w-10 h-1 rounded-full bg-gray-200" />
                </div>
                <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
                    <p className="font-bold text-gray-900 flex items-center gap-2"><FaFilter className="text-red-400" /> Filtri</p>
                    <button onClick={onClose} className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200">
                        <FaTimes className="text-sm" />
                    </button>
                </div>
                <div className="px-5 py-5 overflow-y-auto max-h-[75vh]">
                    {inner}
                </div>
            </div>
        </>
    );
};

/* ─────────────────────────────────────────────
   DESKTOP TABLE COLUMNS
───────────────────────────────────────────── */
const getEmailLabel = (order) => {
    if (getOrderStatusKey(order) === 'annullato') return 'Annullato';
    return order.email_sent ? 'Inviata' : 'No';
};

const getOrderColumns = ({ isAdmin, onView, onResendEmail }) => [
    {
        key: 'InternalOrdNo',
        header: 'N. Ordine',
        headerClassName: 'pl-6 pr-4',
        cellClassName: 'pl-6 pr-4',
        sortValue: (o) => o.InternalOrdNo,
        render: (o) => (
            <>
                <span className={`font-mono font-bold text-sm ${o.has_progettazione ? 'text-blue-600' : 'text-gray-900'}`}>{o.InternalOrdNo}</span>
                {o.ExternalOrdNo && <p className="text-[10px] text-gray-400">APP: {o.ExternalOrdNo}</p>}
            </>
        )
    },
    {
        key: 'OrderDate',
        header: 'Data',
        cellClassName: 'px-4 text-sm text-gray-500',
        sortValue: (o) => (o.OrderDate ? new Date(o.OrderDate).getTime() : null),
        render: (o) => fmtDate(o.OrderDate)
    },
    {
        key: 'Customer',
        header: 'Cliente',
        sortValue: (o) => o.CustomerName || o.Customer,
        render: (o) => (
            <>
                <p className="text-sm font-semibold text-gray-800">{o.CustomerName || o.Customer}{isProspectOrder(o) && <ProspectBadge />}</p>
                <p className="text-xs text-gray-400">{o.Customer}</p>
            </>
        )
    },
    {
        key: 'Salesperson',
        header: 'Agente',
        cellClassName: 'px-4 text-sm text-gray-500',
        sortValue: (o) => o.SalespersonName || o.Salesperson,
        render: (o) => o.SalespersonName || o.Salesperson || '—'
    },
    {
        key: 'email',
        header: 'Email',
        sortValue: getEmailLabel,
        render: (o) => {
            const label = getEmailLabel(o);
            const styles = label === 'Annullato'
                ? 'bg-red-100 text-red-700'
                : label === 'Inviata' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500';
            return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${styles}`}>{label}</span>;
        }
    },
    {
        key: 'total',
        header: 'Totale',
        align: 'right',
        cellClassName: 'px-4 font-bold text-gray-900 text-sm tabular-nums',
        sortValue: (o) => parseFloat(o.PayableAmount || o.total) || 0,
        render: (o) => fmt(o.PayableAmount || o.total)
    },
    {
        key: 'actions',
        header: '',
        align: 'center',
        headerClassName: 'pl-4 pr-6',
        cellClassName: 'pl-4 pr-6',
        render: (o) => (
            <div className="flex items-center justify-center gap-2">
                <button
                    onClick={() => onView(o)}
                    className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-white border border-gray-200 text-gray-600 rounded-lg text-xs font-semibold hover:border-red-300 hover:text-red-600 hover:bg-red-50 transition-all shadow-sm"
                >
                    <FaEye /> Dettagli
                </button>
                {isAdmin && (
                    <button
                        onClick={() => onResendEmail(o)}
                        title="Reinvia email"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 text-gray-600 rounded-lg text-xs font-semibold hover:border-blue-300 hover:text-blue-600 hover:bg-blue-50 transition-all shadow-sm"
                    >
                        <FaEnvelope />
                    </button>
                )}
            </div>
        )
    }
];

const ORDER_SEARCH_KEYS = ['InternalOrdNo', 'ExternalOrdNo', 'CustomerName', 'Customer', 'SalespersonName', 'Salesperson'];
const ORDER_INITIAL_SORT = { key: 'OrderDate', dir: 'desc' };

/* ─────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────── */
const ClientOrdersManagement = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const { dispatch: orderDispatch } = useOrder();
    const isAdmin = user?.role === 'admin';
    // Duplica e Modifica: admin, agenti e funzionari (i clienti no)
    const canDuplicate = isAdmin || user?.userType === 'agent' || user?.role === 'agent' || user?.role === 'head_agent';

    const [orders, setOrders] = useState([]);
    const [mobileSearch, setMobileSearch] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [currentOrder, setCurrentOrder] = useState(null);
    const [orderDetails, setOrderDetails] = useState(null);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [statusFilter, setStatusFilter] = useState('all');
    const [typeFilter, setTypeFilter] = useState('all');
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
    const [dateFrom, setDateFrom] = useState(() => {
        const d = new Date(); d.setMonth(d.getMonth() - 1);
        return d.toISOString().split('T')[0];
    });
    const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);

    const filteredOrders = orders.filter(o => {
        if (typeFilter !== 'all' && (o.order_type || 'ordine') !== typeFilter) return false;
        if (statusFilter !== 'all' && getOrderStatusKey(o) !== statusFilter) return false;
        if (o.OrderDate && (dateFrom || dateTo)) {
            const orderDate = o.OrderDate.slice(0, 10);
            if (dateFrom && orderDate < dateFrom) return false;
            if (dateTo && orderDate > dateTo) return false;
        }
        return true;
    });

    useEffect(() => {
        setDocumentTitle(isAdmin ? 'Gestione Ordini Clienti' : 'I Miei Ordini');
    }, [isAdmin]);

    useEffect(() => {
        if (user) fetchData();
    }, [user]); // eslint-disable-line

    const handleFilterChange = setStatusFilter;
    const handleTypeFilterChange = setTypeFilter;
    const handleDateFromChange = setDateFrom;
    const handleDateToChange = setDateTo;

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const ordersResponse = await api.getOrders(user?.role, user?.id, user?.userType);
            if (ordersResponse.status === 'success' && ordersResponse.orders) {
                const sorted = [...ordersResponse.orders].sort((a, b) => {
                    const da = new Date(a.OrderDate);
                    const db = new Date(b.OrderDate);
                    if (db - da !== 0) return db - da;
                    return (b.InternalOrdNo || '').localeCompare(a.InternalOrdNo || '');
                });
                setOrders(sorted);
            } else {
                throw new Error('Invalid orders response format');
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            Swal.fire({ icon: 'error', title: 'Errore', text: 'Impossibile caricare i dati' });
        } finally {
            setIsLoading(false);
        }
    };

    const fetchOrderDetails = async (orderNumber) => {
        try {
            const response = await api.getOrderItems(orderNumber);
            if (response.status === 'success') {
                return {
                    order: response.order || null,
                    order_items: response.order_items || [],
                    project_files: response.project_files || [],
                    delivery_info: response.delivery_info || null,
                    order_notes: response.order_notes || null
                };
            }
            return { order: null, order_items: [], project_files: [], delivery_info: null, order_notes: null };
        } catch (error) {
            console.error('Error fetching order details:', error);
            return { order: null, order_items: [], project_files: [], delivery_info: null, order_notes: null };
        }
    };

    const handleViewOrderDetails = async (order) => {
        setCurrentOrder(order);
        setOrderDetails(null);
        setDetailsLoading(true);
        setModalOpen(true);
        const details = await fetchOrderDetails(order.InternalOrdNo);
        if (details.order) {
            setCurrentOrder(prev => ({ ...prev, ...details.order }));
        }
        setOrderDetails(details);
        setDetailsLoading(false);
    };

    const handleDeleteOrder = (order) => {
        Swal.fire({
            title: 'Sei sicuro?',
            text: `Vuoi eliminare l'ordine ${order.InternalOrdNo}?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Sì, elimina!',
            cancelButtonText: 'Annulla'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await api.deleteOrder(order.InternalOrdNo);
                    setOrders(prev => prev.filter(o => o.InternalOrdNo !== order.InternalOrdNo));
                    Swal.fire('Eliminato!', 'L\'ordine è stato eliminato.', 'success');
                } catch (error) {
                    Swal.fire({ icon: 'error', title: 'Errore', text: error.message || 'Impossibile eliminare l\'ordine' });
                }
            }
        });
    };

    const handleResendEmail = async (order) => {
        const result = await Swal.fire({
            title: 'Reinvia email',
            text: `Vuoi reinviare l'email per l'ordine ${order.InternalOrdNo}?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Sì, invia!',
            cancelButtonText: 'Annulla'
        });
        if (!result.isConfirmed) return;

        try {
            const response = await api.resendOrderEmail(order.InternalOrdNo);
            if (response.email_sent) {
                Swal.fire('Inviata!', 'Email reinviata con successo.', 'success');
                setOrders(prev => prev.map(o => o.InternalOrdNo === order.InternalOrdNo ? { ...o, email_sent: 1 } : o));
            } else {
                Swal.fire({ icon: 'warning', title: 'Attenzione', text: 'Invio email fallito.' });
            }
        } catch (error) {
            Swal.fire({ icon: 'error', title: 'Errore', text: error.message || 'Impossibile inviare l\'email' });
        }
    };

    const handleDuplicateOrder = async (order) => {
        const result = await Swal.fire({
            title: 'Duplica e Modifica',
            html: `Vuoi duplicare l'ordine <strong>${order.InternalOrdNo}</strong> come bozza per modificarlo?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Sì, duplica!',
            cancelButtonText: 'Annulla'
        });
        if (!result.isConfirmed) return;

        try {
            Swal.fire({ title: 'Duplicazione in corso...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });

            const isAgentUser = user?.role === 'agent' || user?.role === 'head_agent';
            const createdById = isAgentUser ? user?.id : order.Customer;
            const createdByType = isAgentUser ? 'agent' : 'client';

            const response = await api.duplicateOrder(order.InternalOrdNo, createdById, createdByType, {
                role: user?.role,
                userId: user?.id,
                userType: user?.userType
            });

            if (response.status === 'success') {
                // Prima il tipo (azzera un'eventuale sostituzione precedente), poi il carrello che sostituisce l'originale
                orderDispatch({ type: 'SET_ORDER_TYPE', payload: response.order_type || 'ordine' });
                orderDispatch({
                    type: 'LOAD_DRAFT_ORDER',
                    payload: {
                        id: response.draft_order_id,
                        orderDetails: {},
                        cartItems: response.cart_items || [],
                        customer: response.customer || null,
                        replacesOrderNumber: order.InternalOrdNo
                    }
                });

                Swal.fire({
                    title: 'Ordine duplicato!',
                    html: `Bozza creata. Verrai reindirizzato al carrello per modificare l'ordine.<br><br><small>Ordine originale: ${order.InternalOrdNo}</small>`,
                    icon: 'success',
                    confirmButtonText: 'Vai al carrello',
                    confirmButtonColor: '#cd212a'
                }).then(() => {
                    closeModal();
                    navigate('/order-summary');
                });
            } else {
                Swal.fire({ icon: 'error', title: 'Errore', text: response.message || 'Duplicazione fallita' });
            }
        } catch (error) {
            Swal.fire({ icon: 'error', title: 'Errore', text: error.message || 'Impossibile duplicare l\'ordine' });
        }
    };

    const closeModal = () => { setModalOpen(false); setCurrentOrder(null); setOrderDetails(null); };

    // Loading state
    if (isLoading && orders.length === 0) {
        return (
            <>
                <Header />
                <div className="flex flex-col justify-center items-center min-h-[60vh] gap-3">
                    <div className="w-10 h-10 border-[3px] border-gray-200 border-t-red-500 rounded-full animate-spin" />
                    <p className="text-sm text-gray-400">Caricamento dati...</p>
                </div>
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="min-h-screen bg-gray-50/50">
                <div className="w-full px-3 sm:px-5 lg:px-8 py-6 sm:py-8">

                    {/* Page Header */}
                    <div className="mb-6 sm:mb-8">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3 sm:gap-4">
                                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center shadow-lg shadow-red-100 flex-shrink-0">
                                    <FaClipboardList className="text-white text-lg sm:text-xl" />
                                </div>
                                <div>
                                    <h1 className="text-xl sm:text-2xl lg:text-3xl font-black text-gray-900 leading-tight">
                                        {isAdmin ? 'Gestione Ordini' : 'I Miei Ordini'}
                                    </h1>
                                    <p className="text-xs sm:text-sm text-gray-400 mt-0.5">
                                        {isAdmin ? 'Gestione completa ordini clienti' : 'Panoramica dei tuoi ordini'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Stats Row removed */}

                    {/* Layout: sidebar (desktop) + content */}
                    <div className="flex gap-6">

                        {/* Desktop Sidebar */}
                        <div className="hidden lg:block w-72 flex-shrink-0">
                            <FilterPanel
                                dateFrom={dateFrom} setDateFrom={handleDateFromChange}
                                dateTo={dateTo} setDateTo={handleDateToChange}
                                typeFilter={typeFilter} setTypeFilter={handleTypeFilterChange}
                                statusFilter={statusFilter}
                                setStatusFilter={handleFilterChange}
                                orders={orders}
                                isMobile={false}
                                isOpen={false}
                                onClose={() => {}}
                            />
                        </div>

                        {/* Main Content */}
                        <div className="flex-1 min-w-0">

                            {/* Mobile: filter button */}
                            <div className="flex items-center justify-end gap-3 mb-4 lg:hidden">
                                <button
                                    onClick={() => setFilterDrawerOpen(true)}
                                    className="flex-shrink-0 h-10 px-4 bg-white border border-gray-200 rounded-xl text-sm font-semibold text-gray-600 flex items-center gap-2 shadow-sm hover:border-red-300 hover:text-red-600 transition-all"
                                >
                                    <FaFilter className="text-xs" />
                                    Filtri
                                    {statusFilter !== 'all' && <span className="w-2 h-2 rounded-full bg-red-500" />}
                                </button>
                            </div>

                            {/* Mobile Search */}
                            <div className="lg:hidden mb-3">
                                <div className="relative">
                                    <input
                                        type="text"
                                        placeholder="Cerca ordine, cliente..."
                                        value={mobileSearch}
                                        onChange={(e) => setMobileSearch(e.target.value)}
                                        className="w-full px-4 py-3 pl-10 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-red-400 focus:border-red-400 bg-white"
                                    />
                                    <svg className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                    {mobileSearch && (
                                        <button onClick={() => setMobileSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">&#10005;</button>
                                    )}
                                </div>
                            </div>
                            {/* Mobile Cards */}
                            <div className="lg:hidden space-y-3">
                                {filteredOrders.length === 0 ? (
                                    <div className="bg-white rounded-2xl border border-gray-100 p-10 flex flex-col items-center gap-3 text-center">
                                        <FaBoxOpen className="text-3xl text-gray-300" />
                                        <p className="font-semibold text-gray-600">Nessun ordine trovato</p>
                                        <p className="text-sm text-gray-400">Prova a modificare i filtri</p>
                                        {statusFilter !== 'all' && (
                                            <button onClick={() => handleFilterChange('all')} className="text-sm text-red-500 font-semibold hover:underline">Rimuovi filtro stato</button>
                                        )}
                                    </div>
                                ) : (
                                    filteredOrders.filter(o => {
                                        if (!mobileSearch) return true;
                                        const s = mobileSearch.toLowerCase();
                                        return [o.InternalOrdNo, o.ExternalOrdNo, o.CustomerName, o.Customer, o.SalespersonName, o.Salesperson].some(v => v && String(v).toLowerCase().includes(s));
                                    }).map(o => (
                                        <OrderCard
                                            key={o.InternalOrdNo}
                                            order={o}
                                            onView={handleViewOrderDetails}
                                            onResendEmail={handleResendEmail}
                                            isAdmin={isAdmin}
                                        />
                                    ))
                                )}
                            </div>

                            {/* Desktop Table */}
                            <div className="hidden lg:block bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                                {filteredOrders.length === 0 ? (
                                    <div className="py-16 flex flex-col items-center gap-3 text-center">
                                        <FaBoxOpen className="text-4xl text-gray-200" />
                                        <p className="font-semibold text-gray-500">Nessun ordine trovato</p>
                                        {statusFilter !== 'all' && (
                                            <button onClick={() => handleFilterChange('all')} className="text-sm text-red-500 font-semibold hover:underline mt-1">Mostra tutti</button>
                                        )}
                                    </div>
                                ) : (
                                    <DataGrid
                                        columns={getOrderColumns({ isAdmin, onView: handleViewOrderDetails, onResendEmail: handleResendEmail })}
                                        rows={filteredOrders}
                                        rowKey="InternalOrdNo"
                                        searchKeys={ORDER_SEARCH_KEYS}
                                        searchPlaceholder="Cerca ordine, cliente, agente..."
                                        initialSort={ORDER_INITIAL_SORT}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Mobile Filter Drawer */}
            <FilterPanel
                dateFrom={dateFrom} setDateFrom={handleDateFromChange}
                dateTo={dateTo} setDateTo={handleDateToChange}
                typeFilter={typeFilter} setTypeFilter={handleTypeFilterChange}
                statusFilter={statusFilter}
                setStatusFilter={handleFilterChange}
                orders={orders}
                isMobile={true}
                isOpen={filterDrawerOpen}
                onClose={() => setFilterDrawerOpen(false)}
            />

            {/* Detail Modal */}
            <Modal
                isOpen={modalOpen}
                onClose={closeModal}
                title="Dettagli Ordine"
                size="90"
            >
                <OrderDetailsView
                    order={currentOrder}
                    orderDetails={orderDetails}
                    detailsLoading={detailsLoading}
                    isAdmin={isAdmin}
                    canDuplicate={canDuplicate}
                    onDelete={handleDeleteOrder}
                    onDuplicate={handleDuplicateOrder}
                    onClose={closeModal}
                />
            </Modal>
        </>
    );
};

/* ─────────────────────────────────────────────
   ORDER DETAILS VIEW (OrdersStatus style)
───────────────────────────────────────────── */
const OrderDetailsView = ({
    order,
    orderDetails,
    detailsLoading,
    isAdmin,
    canDuplicate,
    onDelete,
    onDuplicate,
    onClose
}) => {
    if (detailsLoading) {
        return (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
                <div className="w-10 h-10 border-[3px] border-gray-200 border-t-red-500 rounded-full animate-spin" />
                <p className="text-sm text-gray-400">Caricamento dettagli...</p>
            </div>
        );
    }
    if (!order) return <div className="py-16 text-center text-gray-400 text-sm">Nessun dato disponibile</div>;

    const sk = getOrderStatusKey(order);
    const items = orderDetails?.order_items || [];
    const deliveryInfo = orderDetails?.delivery_info;
    const projectFiles = orderDetails?.project_files || [];
    const orderNotes = order.order_notes || orderDetails?.order_notes;

    // Varianti d'offerta: gli articoli portano variantId/variantTitle in product_data, i totali stanno in order.variants_data
    const parseJson = (value) => {
        try { return value ? (typeof value === 'string' ? JSON.parse(value) : value) : null; } catch (e) { return null; }
    };
    const variantsMeta = Array.isArray(parseJson(order.variants_data)) ? parseJson(order.variants_data) : [];
    const hasVariants = order.order_type === 'offerta' && items.some((it) => parseJson(it.product_data)?.variantId);
    const displayItems = (() => {
        if (!hasVariants) return items;
        const variantOf = (it) => parseJson(it.product_data)?.variantId;
        const order_ids = variantsMeta.map((v) => v.id);
        items.forEach((it) => { const id = variantOf(it); if (id && !order_ids.includes(id)) order_ids.push(id); });
        const out = items.filter((it) => !variantOf(it));
        order_ids.forEach((id, n) => {
            const groupItems = items.filter((it) => variantOf(it) === id);
            if (groupItems.length === 0) return;
            const meta = variantsMeta.find((v) => v.id === id);
            const title = meta?.title || parseJson(groupItems[0].product_data)?.variantTitle || `Variante ${n + 1}`;
            out.push({ __variantHeader: { id, title, number: n + 1, count: groupItems.length } });
            out.push(...groupItems);
            if (meta) out.push({ __variantFooter: meta });
        });
        return out;
    })();

    return (
        <div className="-mx-5 -my-4 sm:-mx-6">
            {/* Header */}
            <div className="px-5 sm:px-8 pt-5 pb-4 border-b border-gray-100">
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <p className="text-[10px] font-semibold tracking-widest uppercase text-gray-400 mb-0.5">
                            {order.order_type === 'offerta' ? 'Offerta' : 'Ordine'}
                        </p>
                        <p className={`text-xl font-bold font-mono ${order.has_progettazione ? 'text-blue-600' : 'text-gray-900'}`}>{order.InternalOrdNo}</p>
                        {order.ExternalOrdNo && (
                            <p className="text-xs text-gray-400 mt-0.5 font-mono">APP: {order.ExternalOrdNo}</p>
                        )}
                        <p className="text-xs text-gray-400 mt-0.5">{fmtDate(order.OrderDate)}</p>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                        <StatusPill statusKey={sk} />
                        {order.email_sent ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-green-100 text-green-700">Email inviata</span>
                        ) : null}
                    </div>
                </div>
            </div>

            {/* Meta grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-gray-100 border-b border-gray-100">
                {[
                    { label: 'Cliente', value: order.CustomerName || order.Customer },
                    { label: 'Codice', value: order.Customer, mono: true },
                    { label: 'Agente', value: order.SalespersonName || order.Salesperson },
                    { label: 'Consegna', value: order.ConfirmedDeliveryDate ? fmtDate(order.ConfirmedDeliveryDate) : null },
                ].map((f, i) => (
                    <div key={i} className={`px-4 sm:px-6 py-3 ${i >= 2 ? 'border-t border-gray-100 sm:border-t-0' : ''}`}>
                        <p className="text-[9px] font-bold tracking-widest uppercase text-gray-400 mb-0.5">{f.label}</p>
                        <p className={`text-sm font-semibold text-gray-900 ${f.mono ? 'font-mono' : ''}`}>
                            {f.value || <span className="text-gray-300 font-normal">—</span>}
                        </p>
                    </div>
                ))}
            </div>

            {/* Nuovo cliente non in anagrafica */}
            {isProspectOrder(order) && (
                <div className="px-5 sm:px-8 py-3 border-b border-gray-100 bg-amber-50/70">
                    <p className="text-[9px] font-bold tracking-widest uppercase text-amber-700 mb-1">Nuovo cliente — non presente in anagrafica</p>
                    <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-700">
                        {(order.prospect_nome || order.prospect_cognome) && (
                            <span>Referente: <strong>{order.prospect_nome} {order.prospect_cognome}</strong></span>
                        )}
                        {order.prospect_email && <span>Email: <strong>{order.prospect_email}</strong></span>}
                        {order.prospect_telefono && <span>Telefono: <strong>{order.prospect_telefono}</strong></span>}
                        {order.prospect_piva && <span>P.IVA: <strong>{order.prospect_piva}</strong></span>}
                    </div>
                </div>
            )}

            {/* Delivery info row (if exists) */}
            {deliveryInfo && (
                <div className="px-5 sm:px-8 py-3 border-b border-gray-100 bg-amber-50/50">
                    <p className="text-[9px] font-bold tracking-widest uppercase text-gray-400 mb-1">
                        <FaTruck className="inline mr-1" /> Consegna
                    </p>
                    <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-700">
                        {(deliveryInfo.deliveryDate || deliveryInfo.requested_delivery_date) && (
                            <span>Data richiesta: <strong>{fmtDate(deliveryInfo.deliveryDate || deliveryInfo.requested_delivery_date)}</strong></span>
                        )}
                        {(deliveryInfo.deliveryAddress || deliveryInfo.delivery_address) && (
                            <span>
                                {deliveryInfo.deliveryAddress || deliveryInfo.delivery_address}
                                {(deliveryInfo.deliveryZip || deliveryInfo.delivery_zip) && `, ${deliveryInfo.deliveryZip || deliveryInfo.delivery_zip}`}
                                {(deliveryInfo.deliveryCity || deliveryInfo.delivery_city) && ` ${deliveryInfo.deliveryCity || deliveryInfo.delivery_city}`}
                                {(deliveryInfo.deliveryProvince || deliveryInfo.delivery_province) && ` (${deliveryInfo.deliveryProvince || deliveryInfo.delivery_province})`}
                            </span>
                        )}
                    </div>
                </div>
            )}

            {/* Order notes */}
            {orderNotes && projectFiles.length === 0 && (
                <div className="px-5 sm:px-8 py-3 border-b border-gray-100 bg-yellow-50/50">
                    <p className="text-[9px] font-bold tracking-widest uppercase text-gray-400 mb-1">Note</p>
                    <p className="text-xs text-gray-700 whitespace-pre-wrap">{orderNotes}</p>
                </div>
            )}

            {/* Project files section */}
            {projectFiles.length > 0 && (
                <div className="px-5 sm:px-8 py-3 border-b border-gray-100 bg-indigo-50/50">
                    <p className="text-[9px] font-bold tracking-widest uppercase text-gray-400 mb-2">Progettazione</p>
                    {(() => {
                        const desc = projectFiles.find(f => f.project_description)?.project_description || orderNotes;
                        return desc && <p className="text-xs text-indigo-700 whitespace-pre-wrap mb-2">{desc}</p>;
                    })()}
                    {/* Show additional project fields from first progettazione item */}
                    {(() => {
                        const progItem = items.find(it => {
                            let pd = null;
                            try { pd = it.product_data ? (typeof it.product_data === 'string' ? JSON.parse(it.product_data) : it.product_data) : null; } catch(e) { return false; }
                            return pd?.processing?.supplyType === 'progettazione';
                        });
                        if (!progItem) return null;
                        let pd = null;
                        try { pd = progItem.product_data ? (typeof progItem.product_data === 'string' ? JSON.parse(progItem.product_data) : progItem.product_data) : null; } catch(e) { return null; }
                        const proc = pd?.processing;
                        if (!proc || (!proc.metriIndicativi && !proc.vuoiGiunzioni && !proc.vuoiCorrimano && !proc.vuoiTappi && !proc.vuoiSpessori && !proc.vuoiCoprifilo && !proc.vuoiLed)) return null;
                        return (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3 text-xs text-gray-700 space-y-1">
                                <p className="font-semibold text-blue-800 text-[10px] uppercase tracking-wider mb-1">Dettagli aggiuntivi</p>
                                {proc.metriIndicativi && <p><span className="font-semibold">Metri indicativi:</span> {proc.metriIndicativi}</p>}
                                {proc.vuoiGiunzioni && <p><span className="font-semibold">Giunzioni:</span> Sì{proc.giunzioniDettaglio ? ` - ${proc.giunzioniDettaglio}` : ''}</p>}
                                {proc.vuoiCorrimano && <p><span className="font-semibold">Corrimano:</span> Sì{proc.corrimanoDettaglio ? ` - ${proc.corrimanoDettaglio}` : ''}</p>}
                                {proc.vuoiTappi && <p><span className="font-semibold">Tappi:</span> Sì</p>}
                                {proc.vuoiSpessori && <p><span className="font-semibold">Spessori:</span> Sì</p>}
                                {proc.vuoiCoprifilo && <p><span className="font-semibold">Coprifilo:</span> Sì{proc.coprifiloDettaglio ? ` - ${proc.coprifiloDettaglio}` : ''}</p>}
                                {proc.vuoiLed && <p><span className="font-semibold">Led:</span> Sì</p>}
                            </div>
                        );
                    })()}
                    <div className="flex flex-wrap gap-2">
                        {projectFiles.map((file, i) => (
                            <a
                                key={i}
                                href={`${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/download-file/${file.filename}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-indigo-200 rounded-lg text-xs font-medium text-indigo-700 hover:bg-indigo-100 transition-colors"
                            >
                                <FaClipboardList className="text-[10px]" />
                                {file.original_filename}
                                {file.file_size && <span className="text-gray-400">({Math.round(file.file_size / 1024)} KB)</span>}
                            </a>
                        ))}
                    </div>
                </div>
            )}

            {/* Items table */}
            <div className="px-5 sm:px-8 pt-4 pb-2">
                <p className="text-[10px] font-bold tracking-widest uppercase text-gray-400">
                    Articoli — {items.length} {items.length === 1 ? 'prodotto' : 'prodotti'}
                </p>
            </div>

            {items.length === 0 ? (
                <div className="mx-5 sm:mx-8 mb-4 py-8 flex items-center justify-center bg-gray-50 rounded-xl border border-gray-100">
                    <p className="text-sm text-gray-400">Nessun articolo trovato</p>
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-sm min-w-[580px]">
                        <thead>
                            <tr className="bg-gray-50 border-y border-gray-100">
                                <th className="pl-5 sm:pl-8 pr-3 py-2.5 text-left text-[9px] font-bold tracking-widest uppercase text-gray-400">Codice</th>
                                <th className="px-3 py-2.5 text-left text-[9px] font-bold tracking-widest uppercase text-gray-400">Descrizione</th>
                                <th className="px-3 py-2.5 text-center text-[9px] font-bold tracking-widest uppercase text-gray-400">UdM</th>
                                <th className="px-3 py-2.5 text-right text-[9px] font-bold tracking-widest uppercase text-gray-400">Qtà</th>
                                <th className="px-3 py-2.5 text-right text-[9px] font-bold tracking-widest uppercase text-gray-400">Prezzo Listino</th>
                                <th className="pl-3 pr-5 sm:pr-8 py-2.5 text-right text-[9px] font-bold tracking-widest uppercase text-gray-400">Totale Riga</th>
                            </tr>
                        </thead>
                        <tbody>
                            {displayItems.map((item, i) => {
                                if (item.__variantHeader) {
                                    const vh = item.__variantHeader;
                                    return (
                                        <tr key={`vh-${vh.id}`} className="bg-blue-50 border-t-2 border-blue-200">
                                            <td colSpan={6} className="pl-5 sm:pl-8 pr-5 py-2.5">
                                                <span className="inline-flex items-center gap-2">
                                                    <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center">{vh.number}</span>
                                                    <span className="font-bold text-blue-900 text-sm">{vh.title}</span>
                                                    <span className="text-xs text-blue-600">{vh.count} {vh.count === 1 ? 'articolo' : 'articoli'}</span>
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                }
                                if (item.__variantFooter) {
                                    const vf = item.__variantFooter;
                                    return (
                                        <tr key={`vf-${vf.id}`} className="bg-blue-50/50 border-t border-blue-100">
                                            <td colSpan={5} className="pl-5 sm:pl-8 pr-3 py-2 text-right text-xs font-semibold text-blue-900">
                                                Totale {vf.title}
                                                {(parseFloat(vf.discountAmount) || 0) + (parseFloat(vf.extraDiscountAmount) || 0) > 0 && (
                                                    <span className="ml-2 font-normal text-gray-500 line-through">{fmt(vf.subtotal)}</span>
                                                )}
                                            </td>
                                            <td className="pl-3 pr-5 sm:pr-8 py-2 text-right text-sm font-bold text-green-700 tabular-nums whitespace-nowrap">{fmt(vf.total)}</td>
                                        </tr>
                                    );
                                }
                                const qtyNum = parseFloat(item.quantity) || 0;
                                const lineTotal = (parseFloat(item.ListPrice) || 0) * (qtyNum || 1);
                                let pd = null;
                                try { pd = item.product_data ? (typeof item.product_data === 'string' ? JSON.parse(item.product_data) : item.product_data) : null; } catch (e) { pd = null; }
                                const processing = pd?.processing || null;
                                // Prodotti principali: dati sotto processing. Accessori a misura: supplyType/customBars a livello item
                                const isAccMisura = !processing?.supplyType && pd?.isAccessoryItem && pd?.supplyType === 'misura';
                                const supplyType = isAccMisura ? 'misura' : processing?.supplyType;
                                const customBars = isAccMisura ? pd?.customBars : processing?.customBars;
                                const supplyLabel = supplyType === 'misura' ? 'A misura' : supplyType === 'barre' ? 'A barre' : supplyType === 'progettazione' ? 'Progettazione' : null;
                                return (
                                    <tr key={item.id || i} className={`border-t border-gray-50 ${i % 2 === 0 ? '' : 'bg-gray-50/40'} hover:bg-red-50/30 transition-colors align-top`}>
                                        <td className="pl-5 sm:pl-8 pr-3 py-3 font-mono text-xs text-gray-600 font-semibold">{item.product_id}</td>
                                        <td className="px-3 py-3 text-gray-800 max-w-[260px]">
                                            <span className="line-clamp-2 text-xs">{item.Description || '—'}</span>
                                            {supplyLabel && (
                                                <span className="mt-1 inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-50 text-blue-700">{supplyLabel}</span>
                                            )}
                                            {supplyType === 'barre' && processing?.barGroups && processing.barGroups.length > 1 && (
                                                <ul className="mt-1 text-[11px] text-gray-600 space-y-0.5">
                                                    {processing.barGroups.map((g, idx) => (
                                                        <li key={idx}>• {g.quantity} {g.quantity === 1 ? 'barra' : 'barre'} da {g.barLength} metri ({g.quantity * g.barLength} mt)</li>
                                                    ))}
                                                    <li className="font-semibold pt-0.5 border-t border-gray-200">Totale: {processing.barGroups.reduce((s, g) => s + g.quantity, 0)} barre — {processing.barGroups.reduce((s, g) => s + g.quantity * g.barLength, 0)} mt</li>
                                                </ul>
                                            )}
                                            {supplyType === 'misura' && Array.isArray(customBars) && customBars.length > 0 && (
                                                <ul className="mt-1 text-[11px] text-gray-600 space-y-0.5">
                                                    {customBars.map((bar, idx) => (
                                                        <li key={idx}>• {bar.quantity || 0} {bar.quantity === 1 ? 'barra' : 'barre'} da {bar.length || 0}mm</li>
                                                    ))}
                                                </ul>
                                            )}
                                            {supplyType === 'progettazione' && processing && (processing.metriIndicativi || processing.vuoiGiunzioni || processing.vuoiCorrimano || processing.vuoiTappi || processing.vuoiSpessori || processing.vuoiCoprifilo || processing.vuoiLed) && (
                                                <div className="mt-1.5 text-[11px] text-gray-600 space-y-0.5 bg-blue-50 rounded p-2">
                                                    {processing.metriIndicativi && <p><span className="font-semibold">Metri indicativi:</span> {processing.metriIndicativi}</p>}
                                                    {processing.vuoiGiunzioni && <p><span className="font-semibold">Giunzioni:</span> Sì{processing.giunzioniDettaglio ? ` - ${processing.giunzioniDettaglio}` : ''}</p>}
                                                    {processing.vuoiCorrimano && <p><span className="font-semibold">Corrimano:</span> Sì{processing.corrimanoDettaglio ? ` - ${processing.corrimanoDettaglio}` : ''}</p>}
                                                    {processing.vuoiTappi && <p><span className="font-semibold">Tappi:</span> Sì</p>}
                                                    {processing.vuoiSpessori && <p><span className="font-semibold">Spessori:</span> Sì</p>}
                                                    {processing.vuoiCoprifilo && <p><span className="font-semibold">Coprifilo:</span> Sì{processing.coprifiloDettaglio ? ` - ${processing.coprifiloDettaglio}` : ''}</p>}
                                                    {processing.vuoiLed && <p><span className="font-semibold">Led:</span> Sì</p>}
                                                </div>
                                            )}
                                            {/* Alba items */}
                                            {pd?.isAlbaItem && (
                                                <div className="mt-1">
                                                    <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-700">Alba</span>
                                                    {pd.description && <p className="text-[11px] text-gray-600 mt-0.5">{pd.description}</p>}
                                                    {pd.ralCode && <span className="inline-block mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-700">RAL {pd.ralCode}</span>}
                                                </div>
                                            )}
                                            {/* Pensiline kit items */}
                                            {pd?.isPensilineKit && (
                                                <div className="mt-1">
                                                    <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold ${pd.isPensilineProgettazione ? 'bg-purple-50 text-purple-700' : 'bg-indigo-50 text-indigo-700'}`}>
                                                        {pd.isPensilineProgettazione ? 'Linea 3 Progettazione' : 'Linea 3 Kit'}
                                                    </span>
                                                    {pd.description && <p className="text-[11px] text-gray-600 mt-0.5">{pd.description}</p>}
                                                    {pd.ralCode && <span className="inline-block mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-700">RAL {pd.ralCode}</span>}
                                                    {pd.isPensilineProgettazione && pd.processing && (
                                                        <div className="mt-1 text-[11px] text-gray-600 space-y-0.5">
                                                            {pd.processing.uploadedFiles?.length > 0 && <p><span className="font-semibold">File:</span> {pd.processing.uploadedFiles.length} caricati</p>}
                                                            {pd.processing.metriIndicativi && <p><span className="font-semibold">Metri ind.:</span> {pd.processing.metriIndicativi}</p>}
                                                            {pd.processing.notes && <p><span className="font-semibold">Note:</span> {pd.processing.notes.substring(0, 50)}{pd.processing.notes.length > 50 ? '...' : ''}</p>}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                            {/* Sfera-N items */}
                                            {pd?.isSferaNItem && (
                                                <div className="mt-1">
                                                    <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold bg-teal-50 text-teal-700">Sfera-N</span>
                                                    {pd.description && <p className="text-[11px] text-gray-600 mt-0.5">{pd.description}</p>}
                                                    {pd.sferaNNote && <p className="text-[11px] text-blue-600 mt-0.5">Nota: {pd.sferaNNote}</p>}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-3 py-3 text-center text-xs text-gray-500">{item.BaseUoM || '—'}</td>
                                        <td className="px-3 py-3 text-right text-xs text-gray-600 tabular-nums font-semibold">{qtyNum ? qtyNum.toLocaleString('it-IT', { maximumFractionDigits: 3 }) : item.quantity}</td>
                                        <td className="px-3 py-3 text-right text-xs text-gray-600 tabular-nums whitespace-nowrap">{item.ListPrice ? fmt(item.ListPrice) : '—'}</td>
                                        <td className="pl-3 pr-5 sm:pr-8 py-3 text-right text-xs font-bold text-gray-900 tabular-nums whitespace-nowrap">{item.ListPrice ? fmt(lineTotal) : '—'}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Totals */}
            <div className="px-5 sm:px-8 py-5 border-t border-gray-100">
                <div className="flex items-end justify-between gap-4">
                    <div className="flex gap-3">
                        <button onClick={onClose} className="text-sm text-gray-400 hover:text-gray-700 underline underline-offset-2 transition-colors">
                            Chiudi
                        </button>
                        {canDuplicate && isEditableStatus(sk) && (
                            <button
                                onClick={() => onDuplicate(order)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-500 text-white rounded-lg text-xs font-semibold hover:bg-blue-600 transition-colors"
                                title="Crea una copia modificabile; al termine l'originale viene annullato"
                            >
                                <FaCopy className="text-[10px]" /> Duplica e Modifica
                            </button>
                        )}
                        {isAdmin && (
                            <button
                                onClick={() => { onClose(); setTimeout(() => onDelete(order), 300); }}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-500 text-white rounded-lg text-xs font-semibold hover:bg-red-600 transition-colors"
                            >
                                <FaBan className="text-[10px]" /> Elimina
                            </button>
                        )}
                    </div>
                    <div className="space-y-1.5">
                        {order.subtotal && (
                            <div className="flex items-center justify-between gap-8 text-xs text-gray-500">
                                <span>{hasVariants ? 'Subtotale medio' : 'Subtotale'}</span>
                                <span className="tabular-nums font-medium">{fmt(order.subtotal)}</span>
                            </div>
                        )}
                        {order.discountPercentage && parseFloat(order.discountPercentage) > 0 && (
                            <div className="flex items-center justify-between gap-8 text-xs text-red-500">
                                <span>Sconto {order.discountPercentage}%</span>
                                <span className="tabular-nums font-medium">-{fmt(order.discountAmount)}</span>
                            </div>
                        )}
                        {order.extraDiscountPercentage && parseFloat(order.extraDiscountPercentage) > 0 && (
                            <div className="flex items-center justify-between gap-8 text-xs text-red-500">
                                <span>Sconto extra {order.extraDiscountPercentage}%</span>
                                <span className="tabular-nums font-medium">-{fmt(order.extraDiscountAmount)}</span>
                            </div>
                        )}
                        {order.total && (
                            <div className="flex items-center justify-between gap-8 text-xs text-gray-500">
                                <span>{hasVariants ? 'Imponibile medio' : 'Imponibile'}</span>
                                <span className="tabular-nums font-medium">{fmt(order.total)}</span>
                            </div>
                        )}
                        {order.TaxAmount && (
                            <div className="flex items-center justify-between gap-8 text-xs text-gray-500">
                                <span>IVA (22%)</span>
                                <span className="tabular-nums font-medium">{fmt(order.TaxAmount)}</span>
                            </div>
                        )}
                        <div className="flex items-center justify-between gap-8 pt-2 border-t border-gray-200">
                            <span className="text-sm font-bold text-gray-900">{hasVariants ? 'Totale medio' : 'Totale'}</span>
                            <span className="text-lg font-black text-gray-900 tabular-nums">
                                {fmt(order.PayableAmount || order.total)}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Status summary */}
            <div className="px-5 sm:px-8 py-4 border-t border-gray-50 bg-gray-50/50">
                <div className="flex items-center justify-center gap-6">
                    {[
                        { label: 'Consegnato', active: order.Delivered === '1', icon: FaTruck, color: 'green' },
                        { label: 'Fatturato', active: order.Invoiced === '1', icon: FaFileInvoice, color: 'green' },
                        { label: order.Cancelled === '1' ? 'Annullato' : 'Attivo', active: order.Cancelled !== '1', icon: order.Cancelled === '1' ? FaBan : FaCheckCircle, color: order.Cancelled === '1' ? 'red' : 'green' },
                    ].map((s, i) => (
                        <div key={i} className="flex items-center gap-1.5">
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center ${s.active ? `bg-${s.color}-100 text-${s.color}-600` : 'bg-gray-100 text-gray-400'}`}>
                                <s.icon className="text-[10px]" />
                            </div>
                            <span className={`text-[11px] font-medium ${s.active ? 'text-gray-700' : 'text-gray-400'}`}>{s.label}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default ClientOrdersManagement;
